// SortableItemLanguage.tsx

import React from 'react'
import { Trans } from 'react-i18next'
import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import clsx from 'clsx'

import { Reorder16, Trash16 } from '@makyoapp/makyo-icon/icon'
import { MaterialIcon } from '@makyoapp/makyo-tw-ui'
import { Badge, Button, ButtonVariant, ColorVariant, MoreDropdown } from '@makyoapp/makyo-ui'

export interface SortableItemProps {
  id: string
  title?: React.ReactNode
  children?: React.ReactNode
  reorderTitle?: string
  onDelete?: () => void
  onMakeDefault?: () => void
  isDefault?: boolean
  className?: string
  itemClassName?: string
}

export function SortableItemLanguage({
  id,
  title,
  reorderTitle,
  onDelete,
  isDefault = false,
  className,
  itemClassName,
  onMakeDefault,
}: SortableItemProps) {
  const { attributes, listeners, setNodeRef, transition, transform } = useSortable({ id })

  const handleDeleteClick = (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    if (isDefault) {
      event.preventDefault()
      event.stopPropagation()
      return
    }
    if (onDelete) {
      onDelete()
    }
  }

  const handleMakeDefaultClick = (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    if (isDefault) {
      event.preventDefault()
      event.stopPropagation()
      return
    }
    if (onMakeDefault) {
      onMakeDefault()
    }
  }

  return (
    <div
      ref={setNodeRef}
      {...attributes}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
      }}
      className={clsx(
        'flex items-center justify-between h-10 px-2 rounded-md hover:bg-zinc-200 dark:hover:bg-slate-700',
        className
      )}
    >
      {/* Left Section: Country Flag and Name */}
      <div className='flex items-center gap-2'>{title}</div>

      {/* Right Section: Badge, Menu, and Reorder Icon */}
      <div
        className={clsx(
          'flex items-center gap-2 shrink-0',
          'ltr:pr-2 rtl:pl-2', // Apply padding based on text direction
          itemClassName
        )}
      >
        {isDefault && (
          <Badge color={ColorVariant.Teal} className='text-sm'>
            <Trans>Default</Trans>
          </Badge>
        )}

        <MoreDropdown menuClassName='z-10' portalMenu placement='left' menuBtnClassName='w-8 h-8 flex-cc'>
          <MoreDropdown.MenuItem
            onClick={handleMakeDefaultClick}
            className={isDefault ? 'cursor-not-allowed opacity-50' : ''}
          >
            <MaterialIcon size={20} icon='star' />
            <span className='text-sm'>
              <Trans>Make Default</Trans>
            </span>
          </MoreDropdown.MenuItem>
          <MoreDropdown.MenuItem
            onClick={handleDeleteClick}
            className={isDefault ? 'cursor-not-allowed opacity-50' : ''}
          >
            <Trash16 />
            <span className='text-sm'>
              <Trans>Delete</Trans>
            </span>
          </MoreDropdown.MenuItem>
        </MoreDropdown>

        <div {...listeners}>
          <Button
            variant={ButtonVariant.Ghost}
            icon={<Reorder16 />}
            noPadding
            squared
            title={reorderTitle}
            className='hover:text-primary-500!'
            noTip
          />
        </div>
      </div>
    </div>
  )
}
